import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Popover,
  Drawer,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * 状態ラベル定義（Single Source of Truth）
 */
const STATUS_LABELS = {
  active: "希望条件を公開中",
  inactive: "希望条件を非公開",
};

/**
 * 選択用カード（共通）
 */
const OptionCard = ({ selected, icon, title, description, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      cursor: "pointer",
      border: "1px solid",
      borderColor: selected ? "#9CA3AF" : "#E5E7EB",
      backgroundColor: selected ? "#F3F4F6" : "#fff",
      transition: "all 0.2s",
      "&:hover": { backgroundColor: "#F9FAFB" },
    }}
  >
    {/* 左アイコン */}
    <Box sx={{ color: "#4B5563", mt: "2px" }}>{icon}</Box>

    {/* タイトル・説明 */}
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: "0.75rem",
            lineHeight: 1.6,
            color: "text.secondary",
          }}
        >
          {description}
        </Typography>
      )}
    </Box>

    {/* 選択チェック */}
    {selected && (
      <CheckIcon sx={{ fontSize: 18, color: "#4B5563", mt: "2px" }} />
    )}
  </Box>
);

/**
 * ユーザー側：希望条件 公開 / 非公開 セレクター
 *
 * @param {boolean} isActive
 * @param {(next:boolean) => void} onToggle
 */
const RequestSelector = ({ isActive, onToggle }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 現在の状態ラベル
  const triggerLabel = useMemo(
    () => (isActive ? STATUS_LABELS.active : STATUS_LABELS.inactive),
    [isActive]
  );

  // 説明文
  const inactiveDescription = (
    <>
      希望条件とあなたのスキルシートはスキルログ運営局に非公開です。
      <br />
      プロジェクトの閲覧はできますが、運営局からの連絡は届きません。
      <br />
      運営局に相談したい場合や紹介を希望する場合は「公開」に設定してください。
    </>
  );

  const activeDescription = (
    <>
      希望条件とあなたのスキルシートはスキルログ運営局に公開中です。
      <br />
      「詳細を聞きたい」ボタンから運営局に直接コンタクトできます。
      <br />
      条件に合うプロジェクトの紹介もあわせて受け取れます。
    </>
  );

  // 開閉
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // 選択
  const handleSelect = (next) => {
    // 同じ状態なら何もしない
    if (next === isActive) {
      handleClose();
      return;
    }

    // 状態が変わるときだけ実行
    onToggle(next);
    handleClose();
  };

  // Popover / Drawer 共通中身
  const SelectorContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
        公開設定
      </Typography>

      <OptionCard
        selected={!isActive}
        onClick={() => handleSelect(false)}
        icon={<LockIcon />}
        title={STATUS_LABELS.inactive}
        description={inactiveDescription}
      />

      <OptionCard
        selected={isActive}
        onClick={() => handleSelect(true)}
        icon={<LockOpenIcon />}
        title={STATUS_LABELS.active}
        description={activeDescription}
      />
    </Box>
  );

  return (
    <>
      {/* ===== トリガーボタン ===== */}
      <Button
        onClick={handleOpen}
        sx={{
          position: "relative",
          height: 40,
          borderRadius: "999px",

          px: { xs: 3, sm: 6 },

          minWidth: { sm: 220 },
          width: { xs: "100%", sm: 220 },

          color: "#374151",
          bgcolor: "#fff",
          border: "1px solid #E5E7EB",
          boxShadow: 4,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.8,

          "&:hover": { bgcolor: "#F9FAFB" },
        }}
      >
        {/* 左のアイコン */}
        <Box
          sx={{
            position: "absolute",
            left: 6,
            width: 30,
            height: 30,
            borderRadius: "50%",
            backgroundColor: "transparent",
            border: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isActive ? <LockOpenIcon /> : <LockIcon />}
        </Box>

        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            lineHeight: 1,
            pl: 2,
            whiteSpace: "nowrap",
          }}
        >
          {triggerLabel}
        </Typography>

        {/* 右の下矢印アイコン */}
        <Box
          sx={{
            position: "absolute",
            right: 10,
            display: "flex",
            alignItems: "center",
            color: "#9CA3AF",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ExpandMoreIcon />
        </Box>
      </Button>

      {/* ===== PC：Popover / スマホ：BottomSheet ===== */}
      {!isMobile ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1,
              p: 1.5,
              borderRadius: 2.5,
              width: 360,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          }}
        >
          {SelectorContent}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              p: 2,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          }}
        >
          {/* つまみ */}
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: "#D1D5DB",
              borderRadius: 999,
              mx: "auto",
              mb: 2,
            }}
          />
          {SelectorContent}
        </Drawer>
      )}
    </>
  );
};

export default RequestSelector;
